import { faAngleRight as lightAngleRight } from "@jewlr/pro-light-svg-icons/faAngleRight"
import { FloatInput } from "@jewlr/storybook/form"
import PropTypes from "prop-types"
import { useForm, Controller } from "react-hook-form"
import { connect } from "react-redux"

import { getShipEstimate, updateShipEstView } from "areas/product/store/actions"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { IS_NA, IS_US, jewlr_t, IS_JEWLR, IS_PORTAL } from "helpers/application"
import { cn } from "styles/utils"

const ContentHeading = ({ className = "", ...props }) => (
  <div
    className={cn(
      "bg-polar mb-15 tablet:flex-row tablet:mb-10 flex flex-col items-center px-20 py-10",
      className
    )}
    {...props}
  />
)

ContentHeading.propTypes = {
  className: PropTypes.string,
}

const HeadingTitle = ({ className = "", ...props }) => (
  <h4
    className={cn(
      "text-14 tracking-half mr-20 font-bold text-black",
      className
    )}
    {...props}
  />
)
HeadingTitle.propTypes = {
  className: PropTypes.string,
}

const HeadingLink = (props) => (
  <Button
    className="text-link text-15 tracking-half items-end"
    text
    {...props}
  />
)

const IntroText = ({ className = "", ...props }) => (
  <div
    className={cn(
      "text-15 tracking-half leading-21 tablet:text-left mb-20 whitespace-pre-wrap text-center",
      className
    )}
    {...props}
  />
)

IntroText.propTypes = {
  className: PropTypes.string,
}

const Th = ({ className = "", ...props }) => (
  <th
    className={cn(
      "text-15 tracking-half text-grey-35 tablet:font-bold py-5 pr-5 text-left font-medium",
      className
    )}
    {...props}
  />
)

Th.propTypes = {
  className: PropTypes.string,
}

const Td = ({ className = "", ...props }) => (
  <td
    className={cn(
      "text-15 tracking-half text-grey-65 py-5 pr-5 text-left",
      className
    )}
    {...props}
  />
)

Td.propTypes = {
  className: PropTypes.string,
}
const NoWrap = (props) => <span className="whitespace-nowrap" {...props} />

const ShipForm = ({ ship, submit }) => {
  const is_us = IS_US()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      country: ship.hidden_form.country,
      pkg: ship.hidden_form.pkg,
      po_box: ship.po_checked ? ship.po_checked : "",
      portal: IS_PORTAL(),
      postal_code: ship.postal_code,
      ship_date: ship.hidden_form.ship_date,
      shipper: ship.hidden_form.shipper,
      style_code: ship.hidden_form.style_code,
    },
    mode: "onBlur",
    resolver: async (values) => {
      const errors = {}
      const pattern = new RegExp(ship.zip_pattern)

      if (!values.postal_code) {
        errors.postal_code =
          "Please enter your " +
          jewlr_t("address.zip").toLowerCase() +
          (is_us ? " code" : "")
      } else if (
        !pattern.test(
          is_us ? (values.postal_code || "").trim() : values.postal_code
        )
      ) {
        errors.postal_code = "Invalid format"
      }

      return {
        errors,
        values,
      }
    },
  })

  const onSubmit = async (values) => {
    try {
      if (!values.po_box) {
        delete values.po_box
      }
      await new Promise((res, rej) => {
        submit(values, {
          onError: (err) => rej(err),
          onSuccess: () => res(),
        })
      })
    } catch (err) {
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  return (
    <div>
      {false && sl.config.show_guaranteed && (
        <div className="text-guaranteed text-14 tracking-half tablet:text-left mt-20 text-center font-bold">
          ALL ITEMS ARE GUARANTEED FOR DELIVERY BEFORE CHRISTMAS
        </div>
      )}
      <IntroText>
        To find out when you will receive your item, enter your{" "}
        {jewlr_t("address.zip").toLowerCase() + (is_us ? " code" : "")} below to
        view <NoWrap>your delivery options.</NoWrap>
      </IntroText>
      <ContentHeading className="tablet:flex hidden">
        <HeadingTitle>When will I receive my order?</HeadingTitle>
      </ContentHeading>
      <div className="mb-25 mt-10">
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors?.root?.error && (
            <p className="text-error mb-8">{errors.root.error.message}</p>
          )}
          <Controller
            control={control}
            name="postal_code"
            render={({ field, fieldState: { error } }) => (
              <FloatInput
                autoComplete="postal-code"
                data-cy="postal-code-field"
                error={error}
                id="postal_code"
                label={
                  "Enter your " +
                  jewlr_t("address.zip").toLowerCase() +
                  (is_us ? " code" : "") +
                  " here"
                }
                mb="8px"
                p="0"
                type="text"
                {...field}
              />
            )}
          />
          <div className="tablet:items-start flex w-full flex-col items-center">
            <Controller
              control={control}
              name="po_box"
              render={({ field }) => (
                <label className="tablet:mt-0 mb-16 mt-16 inline-flex items-center">
                  <input
                    className="mr-8 align-middle"
                    data-cy="delivery-dates-input"
                    defaultChecked={!!ship.po_checked}
                    type="checkbox"
                    {...field}
                  />
                  This is a PO Box
                </label>
              )}
            />
            <Button
              className="tablet:uppercase tablet:px-32 tablet:py-16 tablet:my-0 leading-20 my-16 h-auto px-24 py-8 font-normal"
              data-cy="getting-delivery-dates"
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
              variant="secondary"
            >
              {isSubmitting ? "Getting Dates..." : "Get Delivery Dates"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
ShipForm.propTypes = {
  ship: PropTypes.object,
  submit: PropTypes.func,
  theme: PropTypes.object,
}

const ResultTable = ({ changeView, ship, onNext }) => {
  return (
    <div>
      <IntroText className="tablet:flex">
        <div>
          Every {IS_PORTAL() ? "Safyre Labs" : sl.config.site_title} item is
          custom made just for you.{" "}
        </div>
        <div>
          The receive date(s) includes custom manufacturing{" "}
          <NoWrap>time and delivery.</NoWrap>
        </div>
      </IntroText>
      <ContentHeading>
        <HeadingTitle className="tablet:block hidden">
          Delivery Dates to{" "}
          <span className="text-primary">{ship.postal_code}</span>
        </HeadingTitle>
        <span className="text-15 tracking-half leading-21 tablet:hidden mr-20 text-black">
          Delivery Dates to:
        </span>
        <p className="text-15 tracking-half leading-21 tablet:hidden mr-20 font-bold text-black">
          {ship.postal_code}
        </p>
        {IS_NA() && (
          <HeadingLink onClick={() => changeView("FORM")} underline>
            Change{" "}
            {jewlr_t("address.zip").toLowerCase() + (IS_US() ? " code" : "")}
            <FontAwesomeIcon className="ml-4" icon={lightAngleRight} />
          </HeadingLink>
        )}
      </ContentHeading>

      <table
        className="tablet:mb-20 tablet:max-w-(--tablet-max-width) mb-40 w-full"
        data-cy="shipping-dates-table"
        style={{ "--tablet-max-width": "400px" }}
      >
        <thead className="border-b-grey-10 border-b">
          <tr>
            <Th>Shipping Method</Th>
            <Th
              className="tablet:w-auto w-(--tablet-header-width)"
              style={{ "--tablet-header-width": "35%" }}
            >
              Receive Date
            </Th>
            <Th
              className="tablet:w-auto w-(--tablet-header-width) text-right"
              style={{ "--tablet-header-width": "15%" }}
            >
              Cost
            </Th>
          </tr>
        </thead>
        <tbody className="align-top">
          {ship.methods
            .filter((method) => method.code !== "pickup")
            .map((method, idx) => (
              <tr key={`shipTable-${method.label}`}>
                <Td data-cy={`shipping-method-${idx}`}>
                  <div className="tablet:block flex flex-col">
                    <span>
                      {method.charge_text.toLowerCase() == "free" && (
                        <strong>Free </strong>
                      )}
                      {method.label}
                    </span>
                    {method.carrier && (
                      <NoWrap>{` (${method.carrier})`}</NoWrap>
                    )}
                  </div>
                </Td>
                <Td>{method.receive_dates}</Td>
                <Td className="text-right">
                  {method.charge_text.toLowerCase() == "free" ? (
                    <span className="text-guaranteed">Free</span>
                  ) : (
                    method.charge_text
                  )}
                </Td>
              </tr>
            ))}
        </tbody>
      </table>
      {IS_JEWLR() && sl.config.enable_gift_preview_video && (
        <div className="tablet:flex-row flex flex-col items-center">
          <p className="text-15 leading-21 whitespace-pre-wrap">
            Need something sooner?{" "}
          </p>
          <HeadingLink
            onClick={(e) => {
              if (onNext) {
                e.preventDefault()
                onNext()
              }
            }}
          >
            We have a solution
            <FontAwesomeIcon className="ml-4" icon={lightAngleRight} />
          </HeadingLink>
        </div>
      )}
    </div>
  )
}

ResultTable.propTypes = {
  changeView: PropTypes.func,
  onNext: PropTypes.func,
  ship: PropTypes.object,
}
const InternalShipPop = ({ changeView, ship, submit, theme, onNext }) => {
  if (!ship) {
    return null
  }

  if (ship.view_type == "OUT_OF_STOCK") {
    return (
      <div>
        We&apos;re sorry, this item is currently unavailable. Please check back
        soon!
      </div>
    )
  }
  return (
    <div className="flex flex-col overflow-hidden">
      <div>
        {IS_NA() && ship.view_type === "FORM" && !!ship.hidden_form && (
          <ShipForm ship={ship} submit={submit} theme={theme} />
        )}
        {ship.view_type === "RESULTS" && (
          <ResultTable changeView={changeView} onNext={onNext} ship={ship} />
        )}
      </div>
      {!ship.view_type && (
        <p className="leading-14 text-12">
          Order now and it will ship by <strong>{ship.esd}</strong>.
        </p>
      )}
    </div>
  )
}

InternalShipPop.propTypes = {
  changeView: PropTypes.func,
  onNext: PropTypes.func,
  ship: PropTypes.object,
  submit: PropTypes.func,
  theme: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    ship: state.wizard.shipping ? state.wizard.shipping.estimator : null,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    changeView: (view) => {
      dispatch(updateShipEstView(view))
    },
    submit: (values, actions) => {
      dispatch(getShipEstimate(values, actions))
    },
  }
}

const ShipPop = connect(mapStateToProps, mapDispatchToProps)(InternalShipPop)

export default ShipPop
